/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * Database class for database operations
 */
export class Database {
  /**
   * Database name
   */
  databaseName: string;

  /**
   * Spanner session
   */
  session: string = "";

  constructor(databaseName: string) {
    this.databaseName = databaseName;
  }

  /**
   * Create Spanner session
   * @param accessToken 
   */
  createSession = async (accessToken: string): Promise<void> => {
    try {
      const response = await fetch(
        `https://spanner.googleapis.com/v1/${this.databaseName}/sessions`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + accessToken,
          },
        }
      );
      if (!response.ok) {
        throw response;
      }
      const json = await response.json();
      this.session = json.name;
    } catch (error) {
      console.log("Failed on creating session");
      console.log(error);
    }
  };

  /**
   * Read database values using sql
   * @param accessToken 
   * @param sql 
   * @returns 
   */
  read = async (accessToken: string, sql: string): Promise<Array<any>> => {
    const executeSqlResponse = await this.executeSql(accessToken, sql);
    console.log("executeSqlResponse", executeSqlResponse);
    const rows: any[] = [];
    if (executeSqlResponse.rows) {
      executeSqlResponse.rows.forEach((rowEntry: Array<any>) => {
        let row: any = {};
        for (const [
          index,
          field,
        ] of executeSqlResponse.metadata.rowType.fields.entries()) {
          row[field.name] = rowEntry[index];
        }
        rows.push(row);
      });
    }
    return rows;
  };

  /**
   * Read-write database values using sql
   * @param accessToken 
   * @param sql 
   * @returns 
   */
  readWrite = async (accessToken: string, sql: string): Promise<Array<any>> => {
    const transaction = {
      begin: {
        readWrite: {
          readLockMode: "PESSIMISTIC",
        },
      },
    };
    const executeSqlResponse = await this.executeSql(
      accessToken,
      sql,
      transaction
    );
    console.log("executeSqlResponse", executeSqlResponse);
    const transactionID = executeSqlResponse.metadata.transaction.id;
    const commitTransactionResponse = await this.commitTransaction(
      accessToken,
      transactionID
    );
    console.log("commitTransactionResponse", commitTransactionResponse);
    const rows: any[] = [];
    if (executeSqlResponse.rows) {
      executeSqlResponse.rows.forEach((rowEntry: Array<any>) => {
        let row: any = {};
        for (const [
          index,
          field,
        ] of executeSqlResponse.metadata.rowType.fields.entries()) {
          row[field.name] = rowEntry[index];
        }
        rows.push(row);
      });
    }
    return rows;
  };

  /**
   * Commit sql transaction
   * @param accessToken 
   * @param transactionId 
   * @returns 
   */
  commitTransaction = async (
    accessToken: string,
    transactionId: string
  ): Promise<any> => {
    if (!this.session) {
      await this.createSession(accessToken);
    }
    try {
      const response = await fetch(
        `https://spanner.googleapis.com/v1/${this.session}:commit`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + accessToken,
          },
          body: JSON.stringify({
            transactionId: transactionId,
          }),
        }
      );
      if (!response.ok) {
        throw response;
      }
      const json = await response.json();
      return json;
    } catch (error) {
      console.log("Failed committing transaction");
      console.log(error);
      return [];
    }
  };

  executeSql = async (
    accessToken: string,
    sql: string,
    transaction: any = undefined
  ): Promise<any> => {
    if (!this.session) {
      await this.createSession(accessToken);
    }
    try {
      const response = await fetch(
        `https://spanner.googleapis.com/v1/${this.session}:executeSql`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + accessToken,
          },
          body: JSON.stringify({
            sql: sql,
            transaction,
          }),
        }
      );
      if (!response.ok) {
        throw response;
      }
      const json = await response.json();
      return json;
    } catch (error) {
      console.log("Failed executing SQL query");
      console.log(error);
      return [];
    }
  };
}
